import { useCallback, useEffect, useRef } from "react";
import { postAcademyProgress } from "../../services/academyApi";
import { trackAcademyEvent } from "../../services/academyTracker";

const SCROLL_MILESTONES = [25, 50, 75, 100];
const FLUSH_INTERVAL_MS = 15000;
const SCROLL_DEBOUNCE_MS = 400;

function getScrollContainer() {
  return document.querySelector(".training-body");
}

function calcScrollPct(container) {
  if (!container) return 0;
  const maxScroll = container.scrollHeight - container.clientHeight;
  if (maxScroll <= 0) return 100;
  return Math.min(100, Math.round((container.scrollTop / maxScroll) * 100));
}

export function useLessonEngagement({ lesson, moduleId, enabled, videoRef }) {
  const maxScrollRef = useRef(0);
  const maxWatchRef = useRef(0);
  const pendingTimeRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const milestonesHitRef = useRef(new Set());
  const flushingRef = useRef(false);

  const flushProgress = useCallback(
    async (opts = {}) => {
      if (!lesson?.id || flushingRef.current) return;

      const isVideo = lesson.lesson_type === "video";
      const engagementPct = isVideo ? maxWatchRef.current : maxScrollRef.current;
      const timeDelta = pendingTimeRef.current;
      pendingTimeRef.current = 0;

      if (!opts.force && !opts.lesson_complete && engagementPct === 0 && timeDelta === 0) {
        return;
      }

      flushingRef.current = true;
      try {
        await postAcademyProgress({
          lesson_id: lesson.id,
          watch_pct: Math.min(100, engagementPct),
          watch_position_sec: isVideo
            ? Math.floor(videoRef?.current?.currentTime || 0)
            : 0,
          time_spent_sec: timeDelta,
          lesson_complete: !!opts.lesson_complete,
        });
      } catch {
        pendingTimeRef.current += timeDelta;
      } finally {
        flushingRef.current = false;
      }
    },
    [lesson, videoRef]
  );

  useEffect(() => {
    if (!enabled || !lesson?.id) return undefined;

    maxScrollRef.current = lesson.watch_pct || 0;
    maxWatchRef.current = lesson.watch_pct || 0;
    milestonesHitRef.current = new Set();
    lastTickRef.current = Date.now();
    pendingTimeRef.current = 0;

    const tick = () => {
      if (document.visibilityState !== "visible") {
        lastTickRef.current = Date.now();
        return;
      }
      const now = Date.now();
      const deltaSec = Math.floor((now - lastTickRef.current) / 1000);
      if (deltaSec > 0) {
        pendingTimeRef.current += deltaSec;
      }
      lastTickRef.current = now;
    };

    const tickInterval = setInterval(tick, 1000);
    const flushInterval = setInterval(() => flushProgress(), FLUSH_INTERVAL_MS);
    flushProgress({ force: true });

    return () => {
      clearInterval(tickInterval);
      clearInterval(flushInterval);
      tick();
      flushProgress({ force: true });
    };
  }, [enabled, lesson?.id, lesson?.watch_pct, flushProgress]);

  useEffect(() => {
    if (!enabled || !lesson?.id) return undefined;

    const container = getScrollContainer();
    if (!container) return undefined;

    let debounceTimer;
    const onScroll = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const pct = calcScrollPct(container);
        if (pct <= maxScrollRef.current) return;

        maxScrollRef.current = pct;
        SCROLL_MILESTONES.forEach((m) => {
          if (pct >= m && !milestonesHitRef.current.has(`s${m}`)) {
            milestonesHitRef.current.add(`s${m}`);
            trackAcademyEvent("lesson_scroll", {
              module_id: moduleId,
              lesson_id: lesson.id,
              scroll_pct: m,
            });
          }
        });
      }, SCROLL_DEBOUNCE_MS);
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      container.removeEventListener("scroll", onScroll);
      clearTimeout(debounceTimer);
    };
  }, [enabled, lesson?.id, moduleId]);

  useEffect(() => {
    if (!enabled || lesson?.lesson_type !== "video") return undefined;

    let debounceTimer;
    let detach = () => {};

    const bind = () => {
      const video = videoRef?.current;
      if (!video) return;

      const onTimeUpdate = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const dur = video.duration;
          if (!dur || !Number.isFinite(dur)) return;

          const pct = Math.min(100, Math.round((video.currentTime / dur) * 100));
          if (pct <= maxWatchRef.current) return;

          maxWatchRef.current = pct;
          SCROLL_MILESTONES.forEach((m) => {
            if (pct >= m && !milestonesHitRef.current.has(`w${m}`)) {
              milestonesHitRef.current.add(`w${m}`);
              trackAcademyEvent("lesson_watch", {
                module_id: moduleId,
                lesson_id: lesson.id,
                watch_pct: m,
              });
            }
          });
        }, SCROLL_DEBOUNCE_MS);
      };

      video.addEventListener("timeupdate", onTimeUpdate);
      detach = () => {
        video.removeEventListener("timeupdate", onTimeUpdate);
        clearTimeout(debounceTimer);
      };
    };

    bind();
    const timer = setTimeout(bind, 500);

    return () => {
      clearTimeout(timer);
      detach();
    };
  }, [enabled, lesson?.id, lesson?.lesson_type, moduleId, videoRef]);

  const getEngagementPct = useCallback(() => {
    if (!lesson) return 0;
    return lesson.lesson_type === "video" ? maxWatchRef.current : maxScrollRef.current;
  }, [lesson]);

  return { flushProgress, getEngagementPct };
}
